/**
 * Message scheduling logic: which messages an appointment event creates, whether they may be sent
 * (consent, plan, allowance, quiet hours) and when. Pure: the caller persists the result.
 */
import { TEMPLATE_META, type TemplateCall, type TemplateId } from '../adapters/messaging/templates';
import { DAY, HOUR, MINUTE, addDaysToKey, atMinuteOfDay, dayKeyOf, isShabbatWindow, minuteOfDay, monthKeyOf, parseHHMM, startOfDayMs, toIso, toMs, weekdayOfDay } from './time';
import type { ResolvedPlan } from './trial';
import type { Appointment, Client, ConsentPurpose, ConsentRecord, E164, Id, IsoUtc, Message, QuietHours, Service, Settings, SuppressionReason } from './types';

// ---------------------------------------------------------------- consent

/** Active (granted, not revoked) record for the purpose, if any. */
export function activeConsent(records: ConsentRecord[], clientId: Id, purpose: ConsentPurpose): ConsentRecord | undefined {
  return records.find((r) => r.clientId === clientId && r.purpose === purpose && r.channel === 'whatsapp' && r.revokedAt === null);
}
export const hasConsent = (records: ConsentRecord[], clientId: Id, purpose: ConsentPurpose): boolean =>
  activeConsent(records, clientId, purpose) !== undefined;

// ---------------------------------------------------------------- quiet hours

/** May an automatic message go out at `ms`? Daily window plus the Friday 16:00 - Saturday 20:00 block. */
export function isSendAllowed(ms: number, quiet: QuietHours, appointmentStartMs: number | undefined, tz: string): boolean {
  const minutes = minuteOfDay(ms, tz);
  if (minutes < parseHHMM(quiet.start) || minutes >= parseHHMM(quiet.end)) return false;
  if (!quiet.blockShabbat || !isShabbatWindow(ms, tz)) return true;
  return appointmentStartMs !== undefined && isShabbatWindow(appointmentStartMs, tz);
}

/** Earliest allowed instant at or after `ms`. */
export function nextSendTime(ms: number, quiet: QuietHours, appointmentStartMs: number | undefined, tz: string): number {
  if (isSendAllowed(ms, quiet, appointmentStartMs, tz)) return ms;
  const first = dayKeyOf(ms, tz);
  const candidates: number[] = [];
  for (let i = 0; i <= 9; i++) {
    const day = addDaysToKey(first, i);
    candidates.push(atMinuteOfDay(day, parseHHMM(quiet.start), tz));
    if (weekdayOfDay(day) === 6) candidates.push(atMinuteOfDay(day, 20 * 60, tz));
  }
  const hit = candidates.sort((a, b) => a - b).find((c) => c > ms && isSendAllowed(c, quiet, appointmentStartMs, tz));
  return hit ?? ms;
}

/** Latest allowed instant at or before `ms` (15-minute resolution, up to 72 h back). */
export function previousSendTime(ms: number, quiet: QuietHours, appointmentStartMs: number | undefined, tz: string): number | undefined {
  const step = 15 * MINUTE;
  for (let t = Math.floor(ms / step) * step; t > ms - 72 * HOUR; t -= step) {
    if (isSendAllowed(t, quiet, appointmentStartMs, tz)) return t;
  }
  return undefined;
}

// ---------------------------------------------------------------- allowance

const COUNTED: ReadonlySet<string> = new Set(['queued', 'sent', 'delivered']);

/** Automatic messages already counted against the allowance in the month of `atMs`. */
export function allowanceUsed(messages: Message[], atMs: number, tz: string): number {
  const month = monthKeyOf(atMs, tz);
  return messages.filter((m) => COUNTED.has(m.status) && monthKeyOf(toMs(m.scheduledFor), tz) === month).length;
}

// ---------------------------------------------------------------- delivery decision

export interface DeliveryContext {
  template: TemplateId;
  clientId: Id;
  consents: ConsentRecord[];
  plan: ResolvedPlan;
  settings: Settings;
  tz: string;
  /** All business messages (for the allowance counter). */
  messages: Message[];
  desiredAtMs: number;
  nowMs: number;
  appointmentStartMs?: number;
  /** For reminders: pull the send time back before the appointment instead of deferring past it. */
  reminderPolicy?: boolean;
  /** Already counted (a queued message being re-checked at send time). */
  skipAllowance?: boolean;
  /** Extra messages planned in the same batch and not yet persisted. */
  pendingCount?: number;
}
export type Delivery =
  | { status: 'queued'; scheduledFor: IsoUtc; deferredFrom?: IsoUtc }
  | { status: 'suppressed'; reason: SuppressionReason; scheduledFor: IsoUtc }
  | { status: 'skip' };

/** Order of checks: consent, plan (Free = manual wa.me queue), quiet hours, allowance. */
export function decideDelivery(c: DeliveryContext): Delivery {
  const desiredIso = toIso(c.desiredAtMs);
  const meta = TEMPLATE_META[c.template];
  if (!hasConsent(c.consents, c.clientId, meta.purpose)) return { status: 'suppressed', reason: 'no_consent', scheduledFor: desiredIso };
  if (!c.plan.caps.autoMessaging) return { status: 'suppressed', reason: 'plan_free_manual', scheduledFor: desiredIso };
  const at = meta.interactive ? c.desiredAtMs : resolveSendTime(c);
  if (at === undefined) return { status: 'skip' };
  if (!c.skipAllowance && allowanceUsed(c.messages, at, c.tz) + (c.pendingCount ?? 0) >= c.plan.allowance) {
    return { status: 'suppressed', reason: 'allowance_exhausted', scheduledFor: toIso(at) };
  }
  return at === c.desiredAtMs
    ? { status: 'queued', scheduledFor: desiredIso }
    : { status: 'queued', scheduledFor: toIso(at), deferredFrom: desiredIso };
}

function resolveSendTime(c: DeliveryContext): number | undefined {
  const { desiredAtMs: want, settings, tz, appointmentStartMs: start } = c;
  const forward = nextSendTime(want, settings.quietHours, start, tz);
  if (forward === want || !c.reminderPolicy || start === undefined) return forward;
  if (forward + HOUR <= start) return forward;
  const back = previousSendTime(want, settings.quietHours, start, tz);
  if (back !== undefined && back > c.nowMs) return back;
  return forward < start ? forward : undefined;
}

// ---------------------------------------------------------------- planning

export interface PlannedMessage {
  call: TemplateCall;
  to: E164;
  clientId: Id;
  appointmentId: Id;
  purpose: ConsentPurpose;
  dedupeKey: string;
  delivery: Exclude<Delivery, { status: 'skip' }>;
}
export interface MessagePlan { create: PlannedMessage[]; cancelIds: Id[] }

export type AppointmentEvent = 'booked' | 'rescheduled' | 'cancelled_by_owner' | 'cancelled_by_client' | 'deposit_requested' | 'deposit_paid' | 'deposit_expired';

export interface PlanInput {
  event: AppointmentEvent;
  appointment: Appointment;
  /** For 'rescheduled'. */
  previousStart?: IsoUtc;
  client: Client;
  service: Service;
  settings: Settings;
  plan: ResolvedPlan;
  tz: string;
  consents: ConsentRecord[];
  /** Every message of the business (idempotency + allowance). */
  messages: Message[];
  nowMs: number;
  /** Pre-rendered display strings. */
  text: PlanText;
}
/** Formatting and URL callbacks so the planner stays locale- and router-agnostic. */
export interface PlanText {
  businessName: string;
  date(ms: number): string;
  time(ms: number): string;
  money(agorot: number): string;
  manageUrl(token: string): string;
  bookUrl: string;
  payUrl: string;
  /** Optional: "today" / "tomorrow" / "in 3 days" for a whole-day delta (reminder wording from the real send-to-visit distance). */
  relativeDay?(days: number): string;
}

export const REMINDER_TEMPLATE: TemplateId = 'reminder_24h';

/** Queued (or manual-queue) messages of an appointment: stale once the appointment changes. */
function pendingFor(messages: Message[], appointmentId: Id, nowMs: number): Message[] {
  return messages
    .filter((m) => m.appointmentId === appointmentId)
    .filter((m) => m.status === 'queued' || (m.status === 'suppressed' && m.suppressionReason === 'plan_free_manual' && toMs(m.scheduledFor) > nowMs));
}

export function planAppointmentMessages(input: PlanInput): MessagePlan {
  const { event, appointment: a, client, nowMs } = input;
  const startMs = toMs(a.start);
  const base = (): { businessName: string; clientName: string; serviceName: string; date: string; time: string } => ({
    businessName: input.text.businessName, clientName: client.name.split(' ')[0] ?? client.name, serviceName: input.service.name,
    date: input.text.date(startMs), time: input.text.time(startMs),
  });
  const manageUrl = input.text.manageUrl(a.manageToken);
  const wanted: Array<{ call: TemplateCall; key: string; at: number; reminder?: boolean }> = [];
  let stale: Message[] = [];

  const confirmation = (): void => {
    wanted.push({ call: { template: 'booking_confirmed', params: { ...base(), manageUrl } }, key: `booking_confirmed:${a.id}`, at: nowMs });
    const remindAt = startMs - input.settings.reminderOffsetH * HOUR;
    if (input.settings.remindersEnabled && remindAt > nowMs) {
      wanted.push({ call: { template: 'reminder_24h', params: { ...base(), manageUrl } }, key: `reminder_24h:${a.id}:${a.start}`, at: remindAt, reminder: true });
    }
  };

  switch (event) {
    case 'booked':
      if (a.status !== 'awaiting_deposit') confirmation();
      break;
    case 'deposit_requested':
      wanted.push({ call: { template: 'deposit_request', params: { ...base(), amount: input.text.money(a.depositAgorot), payUrl: input.text.payUrl, holdMinutes: String(input.settings.depositHoldMin) } }, key: `deposit_request:${a.id}`, at: nowMs });
      break;
    case 'deposit_paid':
      wanted.push({ call: { template: 'deposit_received', params: { ...base(), amount: input.text.money(a.depositAgorot), manageUrl } }, key: `deposit_received:${a.id}`, at: nowMs });
      confirmation();
      break;
    case 'deposit_expired':
      stale = pendingFor(input.messages, a.id, nowMs);
      wanted.push({ call: { template: 'deposit_expired', params: { ...base(), bookUrl: input.text.bookUrl } }, key: `deposit_expired:${a.id}`, at: nowMs });
      break;
    case 'rescheduled': {
      stale = pendingFor(input.messages, a.id, nowMs);
      const old = toMs(input.previousStart ?? a.start);
      wanted.push({ call: { template: 'rescheduled', params: { ...base(), oldDate: input.text.date(old), oldTime: input.text.time(old), manageUrl } }, key: `rescheduled:${a.id}:${a.start}`, at: nowMs });
      const remindAt = startMs - input.settings.reminderOffsetH * HOUR;
      if (input.settings.remindersEnabled && remindAt > nowMs) {
        wanted.push({ call: { template: 'reminder_24h', params: { ...base(), manageUrl } }, key: `reminder_24h:${a.id}:${a.start}`, at: remindAt, reminder: true });
      }
      break;
    }
    case 'cancelled_by_owner':
    case 'cancelled_by_client': {
      stale = pendingFor(input.messages, a.id, nowMs);
      const template = event === 'cancelled_by_owner' ? 'cancelled_by_owner' : 'cancelled_by_client_ack';
      wanted.push({ call: { template, params: { ...base(), bookUrl: input.text.bookUrl } }, key: `${template}:${a.id}`, at: nowMs });
      break;
    }
  }

  // a pending message that the new plan wants again (same key) is kept, not cancelled and re-created
  const wantedKeys = new Set(wanted.map((w) => w.key));
  const cancelIds = stale.filter((m) => !wantedKeys.has(m.dedupeKey)).map((m) => m.id);
  const live = input.messages.filter((m) => m.status !== 'cancelled' && !cancelIds.includes(m.id));
  const liveKeys = new Set(live.map((m) => m.dedupeKey));
  const create: PlannedMessage[] = [];
  let pending = 0;
  for (const w of wanted) {
    if (liveKeys.has(w.key)) continue;
    const delivery = decideDelivery({
      template: w.call.template, clientId: client.id, consents: input.consents, plan: input.plan, settings: input.settings, tz: input.tz,
      messages: live, desiredAtMs: w.at, nowMs, appointmentStartMs: startMs, reminderPolicy: w.reminder === true, pendingCount: pending,
    });
    if (delivery.status === 'skip') continue;
    if (delivery.status === 'queued') pending++;
    let call = w.call;
    if (call.template === 'reminder_24h' && input.text.relativeDay) {
      const days = Math.round((startOfDayMs(dayKeyOf(startMs, input.tz), input.tz) - startOfDayMs(dayKeyOf(toMs(delivery.scheduledFor), input.tz), input.tz)) / DAY);
      call = { ...call, params: { ...call.params, date: `${input.text.relativeDay(Math.max(0, days))}, ${call.params.date}` } };
    }
    create.push({ call, to: client.phone, clientId: client.id, appointmentId: a.id, purpose: TEMPLATE_META[w.call.template].purpose, dedupeKey: w.key, delivery });
  }
  return { create, cancelIds };
}

// ---------------------------------------------------------------- send-time preflight

export type Preflight =
  | { action: 'send' }
  | { action: 'suppress'; reason: SuppressionReason }
  | { action: 'defer'; scheduledFor: IsoUtc };

/**
 * Re-checks a queued message that has become due: consent may have been revoked since it was
 * planned and quiet hours may apply to the actual send instant.
 */
export function preflightDue(message: Message, ctx: Omit<DeliveryContext, 'template' | 'clientId' | 'desiredAtMs' | 'skipAllowance' | 'messages'> & { template: TemplateId; clientId: Id }): Preflight {
  const d = decideDelivery({ ...ctx, messages: [], desiredAtMs: Math.max(toMs(message.scheduledFor), ctx.nowMs), skipAllowance: true });
  if (d.status === 'suppressed') return { action: 'suppress', reason: d.reason };
  if (d.status === 'skip') return { action: 'send' };
  return toMs(d.scheduledFor) > ctx.nowMs ? { action: 'defer', scheduledFor: d.scheduledFor } : { action: 'send' };
}
